import { useEffect, useState, useMemo } from "react";
import { SettingsProvider } from "./context/SettingsContext";
import { BRAND } from "./config/brand";

import CalculatorForm from "./components/CalculatorForm";
import SummaryPanel from "./components/SummaryPanel";
import ClassificationPanel from "./components/ClassificationPanel";
import InvoicePreview from "./components/InvoicePreview";
import InvoiceDashboard from "./components/InvoiceDashboard";
import ClientDirectory from "./components/ClientDirectory";
import SettingsPanel from "./components/SettingsPanel";
import Footer from "./components/Footer";
import Topbar from "./components/Topbar";
import BrandMark from "./components/BrandMark";

import "./styles/layout.css";

const TABS = [
  { id: "summary", label: "Summary", icon: "📋" },
  { id: "risk", label: "Risk", icon: "⚠️" },
  { id: "invoice", label: "Invoice", icon: "🧾" },
  { id: "clients", label: "Clients", icon: "👥" },
  { id: "admin", label: "Jobs", icon: "📁" }
];

export default function App() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("summary");
  const [clientToApply, setClientToApply] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem("app-theme") || "light");

  const handleUseClient = (client) => {
    setClientToApply(client);
    setTab("summary");
  };

  useEffect(() => {
    document.body.classList.remove("light", "dark");
    document.body.classList.add(theme);
    localStorage.setItem("app-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const route = useMemo(() => {
    if (!data) return {};
    return {
      from: data?.route?.from || "-",
      to: data?.route?.to || "-",
      distanceText: data?.route?.distanceText || data?.distanceText || "-",
      durationText: data?.route?.durationText || data?.durationText || "-",
      durationHours: data?.route?.durationHours || 0
    };
  }, [data]);

  const summary = useMemo(() => {
    const fallbackTotal =
      Number(data?.summary?.total) ||
      Number(data?.invoice?.total) ||
      Number(data?.pricing?.totalCost) ||
      0;
    return {
      ...(data?.summary || {}),
      total: fallbackTotal,
      distanceKm:
        Number(data?.summary?.distanceKm) ||
        Number(data?.route?.distanceKm) ||
        Number(data?.invoice?.route?.distance) ||
        0,
      classification:
        data?.summary?.classification ||
        data?.risk?.classification ||
        data?.invoice?.riskAssessment?.classification ||
        "Unknown",
      riskLevel:
        data?.summary?.riskLevel ||
        data?.risk?.riskLevel ||
        data?.invoice?.riskAssessment?.riskLevel ||
        "unknown"
    };
  }, [data]);

  const load = useMemo(
    () => ({
      weight: data?.invoice?.loadDetails?.weight ?? data?.form?.weight ?? 0,
      width: data?.invoice?.loadDetails?.width ?? data?.form?.width ?? 0,
      height: data?.invoice?.loadDetails?.height ?? data?.form?.height ?? 0,
      machineType: data?.invoice?.loadDetails?.machineType ?? data?.form?.machineDetails?.machineType ?? "",
      machineCategory: data?.invoice?.loadDetails?.machineCategory ?? data?.form?.machineDetails?.machineCategory ?? "",
      trailerType: data?.invoice?.loadDetails?.trailerType ?? data?.form?.machineDetails?.trailerType ?? "",
      axleConfiguration: data?.invoice?.loadDetails?.axleConfiguration ?? data?.form?.machineDetails?.axleConfiguration ?? ""
    }),
    [data]
  );

  return (
    <SettingsProvider>
      <div className={`layout ${theme}`}>
        <aside className="sidebar">
          <div className="sidebar-header">
            <BrandMark variant="sidebar" />
          </div>

          <CalculatorForm
            setData={setData}
            clientToApply={clientToApply}
            onClientApplied={() => setClientToApply(null)}
            onQuoteReady={() => setTab("summary")}
          />

          <div className="sidebar-settings">
            <SettingsPanel />
          </div>
        </aside>

        <main className="main">
          <Topbar toggleTheme={toggleTheme} theme={theme} activeTab={tab} />

          <nav className="app-tabs" aria-label="Main sections">
            {TABS.map(({ id, label, icon }) => (
              <button
                key={id}
                type="button"
                className={`app-tabs__btn ${tab === id ? "active" : ""}`}
                onClick={() => setTab(id)}
              >
                <span className="app-tabs__icon" aria-hidden="true">{icon}</span>
                {label}
              </button>
            ))}
          </nav>

          <div className="content fade-in">
            {tab === "summary" && !data && (
              <div className="empty-state">
                <BrandMark variant="hero" showTagline={false} />
                <h3>Welcome to {BRAND.name}</h3>
                <p>Create a quote in the sidebar to see route, pricing, and risk analysis here.</p>
              </div>
            )}

            {tab === "summary" && data && (
              <SummaryPanel
                summary={summary}
                pricing={data?.pricing || {}}
                risk={data?.risk || {}}
                route={route}
                load={load}
              />
            )}

            {tab === "risk" && (
              <ClassificationPanel risk={data?.risk || {}} />
            )}

            {tab === "invoice" && (
              <InvoicePreview
                invoice={{
                  ...(data?.invoice || {}),
                  risk: data?.risk || data?.invoice?.risk || {},
                  summary: data?.summary || data?.invoice?.summary || {},
                  form: data?.form || data?.invoice?.form || {},
                  client: (() => {
                    const incoming = data?.invoice?.client || {};
                    const fallback = data?.form?.client || {};
                    const isPlaceholder = (value) =>
                      ["client name", "client company", "client@email.com"].includes(
                        String(value || "").trim().toLowerCase()
                      );
                    const pick = (...values) =>
                      values.find((value) => {
                        const normalized = String(value || "").trim();
                        return normalized && !isPlaceholder(normalized);
                      }) || "";
                    return {
                      name: pick(incoming.name, fallback.name),
                      company: pick(incoming.company, fallback.company),
                      email: pick(incoming.email, incoming.contact, fallback.email),
                      phone: pick(incoming.phone, fallback.phone)
                    };
                  })()
                }}
              />
            )}

            {tab === "clients" && (
              <ClientDirectory onUseClient={handleUseClient} />
            )}

            {tab === "admin" && (
              <InvoiceDashboard
                onSelect={(job) => {
                  setData({
                    invoice: job,
                    risk: job?.riskAssessment || {},
                    summary: {
                      total: job?.total || 0,
                      distanceKm: Number(job?.route?.distance || 0) || 0,
                      classification: job?.riskAssessment?.classification || "Unknown",
                      riskLevel: job?.riskAssessment?.riskLevel || "unknown"
                    },
                    pricing: {
                      fuelCost: job?.pricing?.fuelCost || 0,
                      transportCost: job?.pricing?.transportCost || 0,
                      tollCost: job?.pricing?.tollCost || 0,
                      escortCost: job?.pricing?.escortCost || 0,
                      additionalCharges: job?.pricing?.additionalCharges || []
                    },
                    route: {
                      from: job?.route?.from || "-",
                      to: job?.route?.to || "-",
                      distanceText: job?.route?.distance ? `${Math.round(job.route.distance)} km` : "-",
                      durationText: "-",
                      durationHours: 0
                    },
                    form: {
                      from: job?.route?.from || "",
                      to: job?.route?.to || "",
                      weight: job?.loadDetails?.weight || 0,
                      width: job?.loadDetails?.width || 0,
                      height: job?.loadDetails?.height || 0,
                      machineDetails: {
                        machineType: job?.loadDetails?.machineType || "",
                        machineCategory: job?.loadDetails?.machineCategory || "",
                        trailerType: job?.loadDetails?.trailerType || "",
                        axleConfiguration: job?.loadDetails?.axleConfiguration || ""
                      },
                      client: job?.client || {}
                    }
                  });
                  setTab("summary");
                }}
              />
            )}

          </div>

          <Footer />
        </main>
      </div>
    </SettingsProvider>
  );
}
